// Printer alignment for THIS device (browser), per company preset.
//
// Paper geometry belongs to the company preset (server). How far this
// computer's printer shifts the output belongs here. The first time a preset
// is used on a device, the offsets are seeded from the existing label panel's
// browser setting so calibrated printers keep printing in the same place. The
// old setting itself is only read, never written.

import { normalizeDeviceCalibration, normalizeSheetGeometry } from "../../../components/Print/engine/sheetLayout";

const STORAGE_KEY = "bw-print-device-calibration-v1";
export const LEGACY_PANEL_CALIBRATION_KEY = "bw-barcode-print-calibration-v1";

const getStorage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (_error) {
    return null;
  }
};

const readJson = (storage, key) => {
  try {
    return JSON.parse(storage?.getItem(key) || "null");
  } catch (_error) {
    return null;
  }
};

/** The existing label panel's browser settings (geometry + offsets), if any. */
export const readLegacyPanelCalibration = (storage = getStorage()) => {
  const stored = readJson(storage, LEGACY_PANEL_CALIBRATION_KEY);
  if (!stored) return null;
  return { geometry: normalizeSheetGeometry(stored), offsets: normalizeDeviceCalibration(stored) };
};

/**
 * This device's offsets for a preset. The first read seeds them once (from
 * the old label panel's setting, or zero) and stores the result, so later
 * changes to the old panel never move Print Center labels.
 */
export const readDeviceCalibration = (presetId, storage = getStorage()) => {
  const all = readJson(storage, STORAGE_KEY) || {};
  if (all[presetId]) return normalizeDeviceCalibration(all[presetId]);
  const legacy = readLegacyPanelCalibration(storage);
  const seeded = legacy ? legacy.offsets : normalizeDeviceCalibration(null);
  if (storage && presetId != null) {
    try {
      all[presetId] = { ...seeded, seededFrom: legacy ? "label-panel" : "default" };
      storage.setItem(STORAGE_KEY, JSON.stringify(all));
    } catch (_error) {}
  }
  return seeded;
};

/** Where this device's offsets for a preset came from (for the settings drawer). */
export const readDeviceCalibrationSource = (presetId, storage = getStorage()) => {
  const entry = (readJson(storage, STORAGE_KEY) || {})[presetId];
  if (!entry) return null;
  return entry.seededFrom || "saved";
};

export const saveDeviceCalibration = (presetId, offsets, storage = getStorage()) => {
  if (!storage || presetId == null) return;
  try {
    const all = readJson(storage, STORAGE_KEY) || {};
    // Saved by a person: no longer a seed.
    all[presetId] = normalizeDeviceCalibration(offsets);
    storage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch (_error) {}
};

/** Which preset this device prints with (a convenience, not shared state). */
const PRESET_CHOICE_KEY = "bw-print-preset-choice-v1";

export const readPresetChoice = (storage = getStorage()) => {
  const value = Number(storage?.getItem?.(PRESET_CHOICE_KEY));
  return Number.isInteger(value) && value > 0 ? value : null;
};

export const savePresetChoice = (presetId, storage = getStorage()) => {
  try {
    storage?.setItem(PRESET_CHOICE_KEY, String(presetId));
  } catch (_error) {}
};
